// Post-processing (docs/ART-DIRECTION.md §2.5, §4, §7). Two composers give selective bloom:
//   1. emissive composer: EMISSIVE_LAYER only (the globe occluder writes depth) → UnrealBloomPass;
//      its read buffer then holds the emissive objects plus their bloom, over black.
//   2. final composer: BASE_LAYER → + emissive texture → hologram screen pass → OutputPass.
// Only the emissive layer ever blooms; "Reduce effects" disables the hologram screen pass (scanlines,
// chromatic aberration, vignette) and leaves bloom on.

import { Color, HalfFloatType, ShaderMaterial, Vector2, WebGLRenderTarget, type Camera, type Scene, type Texture, type WebGLRenderer } from 'three'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js'
import type { Pass } from 'three/examples/jsm/postprocessing/Pass.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import { BASE_LAYER, EMISSIVE_LAYER } from '../layers'
import type { QualityProfile } from '../visualSettings'
import type { Viewport } from '../viewport'
import { EMISSIVE_COMPOSITE_FRAGMENT, FULLSCREEN_VERTEX, HOLO_SCREEN_FRAGMENT } from './postShaders'

const EMISSIVE_CLEAR_COLOUR = new Color(0x000000)
const OPAQUE_ALPHA = 1
const INITIAL_TARGET_PX = 1

/** User call (2026-09-27): bloom barely there, about 5% — the glow comes from the emissive colours, not the blur. */
const BLOOM_STRENGTH = 0.05
const BLOOM_RADIUS = 0.55
/** Everything on the emissive layer blooms; the layer itself is the selection. */
const BLOOM_THRESHOLD = 0

const SCANLINE_PERIOD_PX = 3
/** Scroll speed in CSS px/s. Each pixel's scanline cycle is speed / period = 1.33 Hz, under 3 Hz (§4). */
const SCANLINE_SCROLL_PX_PER_SEC = 4
const SCANLINE_DEPTH = 0.08
const ABERRATION_STRENGTH = 0.012
const VIGNETTE_START = 0.45
const VIGNETTE_END = 0.8
const VIGNETTE_DEPTH = 0.45

export class PostPipeline {
  private readonly emissiveComposer: EffectComposer
  private readonly finalComposer: EffectComposer
  private readonly passes: readonly Pass[]
  private readonly holoPass: ShaderPass
  private readonly emissiveTexture: { value: Texture | null } = { value: null }
  private readonly pixelRatio = { value: 1 }
  private readonly scanlinePhase = { value: 0 }
  /** The base pass clears to this (space, the palette's background); the pass holds it by reference. */
  private readonly backgroundColour = new Color()

  constructor(
    renderer: WebGLRenderer,
    scene: Scene,
    private readonly camera: Camera,
    profile: QualityProfile,
    viewport: Viewport,
    reduceEffects: boolean,
    background: Color,
  ) {
    this.backgroundColour.copy(background)
    this.emissiveComposer = new EffectComposer(renderer, composerTarget(profile))
    this.emissiveComposer.renderToScreen = false
    const emissivePass = new RenderPass(scene, camera, null, EMISSIVE_CLEAR_COLOUR, OPAQUE_ALPHA)
    const bloomPass = new UnrealBloomPass(new Vector2(viewport.width, viewport.height), BLOOM_STRENGTH, BLOOM_RADIUS, BLOOM_THRESHOLD)
    this.emissiveComposer.addPass(emissivePass)
    this.emissiveComposer.addPass(bloomPass)

    this.finalComposer = new EffectComposer(renderer, composerTarget(profile))
    const basePass = new RenderPass(scene, camera, null, this.backgroundColour, OPAQUE_ALPHA)
    const compositePass = new ShaderPass(
      new ShaderMaterial({
        uniforms: { tDiffuse: { value: null }, tEmissive: this.emissiveTexture },
        vertexShader: FULLSCREEN_VERTEX,
        fragmentShader: EMISSIVE_COMPOSITE_FRAGMENT,
      }),
    )
    this.holoPass = new ShaderPass(
      new ShaderMaterial({
        uniforms: {
          tDiffuse: { value: null },
          uPixelRatio: this.pixelRatio,
          uScanlinePeriodPx: { value: SCANLINE_PERIOD_PX },
          uScanlinePhase: this.scanlinePhase,
          uScanlineDepth: { value: SCANLINE_DEPTH },
          uAberration: { value: ABERRATION_STRENGTH },
          uVignetteStart: { value: VIGNETTE_START },
          uVignetteEnd: { value: VIGNETTE_END },
          uVignetteDepth: { value: VIGNETTE_DEPTH },
        },
        vertexShader: FULLSCREEN_VERTEX,
        fragmentShader: HOLO_SCREEN_FRAGMENT,
      }),
    )
    const outputPass = new OutputPass()
    this.finalComposer.addPass(basePass)
    this.finalComposer.addPass(compositePass)
    this.finalComposer.addPass(this.holoPass)
    this.finalComposer.addPass(outputPass)

    this.passes = [emissivePass, bloomPass, basePass, compositePass, this.holoPass, outputPass]
    this.setReduceEffects(reduceEffects)
    this.setSize(viewport)
  }

  setSize(viewport: Viewport): void {
    for (const composer of [this.emissiveComposer, this.finalComposer]) {
      composer.setPixelRatio(viewport.pixelRatio)
      composer.setSize(viewport.width, viewport.height)
    }
    this.pixelRatio.value = viewport.pixelRatio
  }

  /** Space behind the globe (a retune or palette change). */
  setBackground(background: Color): void {
    this.backgroundColour.copy(background)
  }

  setReduceEffects(reduceEffects: boolean): void {
    this.holoPass.enabled = !reduceEffects
  }

  /** Renders the frame. Temporarily narrows the camera to one layer per composer, then restores BASE_LAYER. */
  render(dtSeconds: number): void {
    this.scanlinePhase.value = (this.scanlinePhase.value + (dtSeconds * SCANLINE_SCROLL_PX_PER_SEC) / SCANLINE_PERIOD_PX) % 1
    this.camera.layers.set(EMISSIVE_LAYER)
    this.emissiveComposer.render(dtSeconds)
    this.camera.layers.set(BASE_LAYER)
    // Read after rendering: the composer's read buffer is where its last pass left the result.
    this.emissiveTexture.value = this.emissiveComposer.readBuffer.texture
    this.finalComposer.render(dtSeconds)
  }

  dispose(): void {
    for (const pass of this.passes) pass.dispose()
    this.emissiveComposer.dispose()
    this.finalComposer.dispose()
  }
}

function composerTarget(profile: QualityProfile): WebGLRenderTarget {
  return new WebGLRenderTarget(INITIAL_TARGET_PX, INITIAL_TARGET_PX, { type: HalfFloatType, samples: profile.msaaSamples })
}
